/**
 * signLicence — RPC contract for the callable Revia staff call from the
 * backoffice to sign a managed client's licence, or re-sign it.
 *
 * Cross-process contract between the backoffice's licence form (caller) and
 * Revia's server `signLicence` handler (onCall). Both ends parse with
 * `.strict()` — an extra key fails the whole request.
 *
 * The caller holds `STAFF_PERMISSION` in `workspaces/revia` and is signed in
 * with a second factor. The server builds Revent's licence payload
 * `{ v: 1, projectId, features, endsAt, graceDays }`, signs its signing input
 * with Revia's licence key (Cloud KMS in production), verifies the composed
 * token against Revent's committed `LICENCE_PUBLIC_KEY_PEM`, the key every
 * client verifies with, and writes `workspaces/revia/licences/{projectId}`
 * (`licence.entity.ts`), replacing any licence signed before. Staff then
 * paste the token as the client environment's `LICENCE_TOKEN` secret, which
 * the client's deploy verifies and writes to its `platform/licence`.
 *
 * Fields justified:
 *   projectId — the client's Google Cloud project id; signed as the
 *               payload's `projectId` and the record's doc id.
 *   client    — the client's display name, stored on the record.
 *   features  — the paid features granted, as Revent's payload names them.
 *   endsAt    — the instant the licence ends, ISO 8601 in UTC.
 *   graceDays — the grace length; omitted or null for Revent's
 *               `LICENCE_GRACE_DAYS`.
 *
 * Output:
 *   projectId   — the project signed for.
 *   environment — the client's GitHub environment the token belongs in:
 *                 derived from `client` at the first signing, kept after.
 *   token       — the signed token, so the form shows it the moment it is
 *                 signed, before the list's listener answers.
 *
 * Errors (thrown HttpsError, not part of the Output schema):
 *   unauthenticated     — no signed-in caller.
 *   permission-denied   — the caller does not hold `STAFF_PERMISSION` in
 *     `workspaces/revia`, or is signed in without a second factor.
 *   invalid-argument    — the input does not parse.
 *   failed-precondition — `endsAt` is not in the future, a first signing's
 *     client name leaves no letter or digit for its environment name
 *     (`client-name-unusable`), the signing key
 *     version is not configured, or the release's public key is still the
 *     placeholder.
 *   internal            — the signature does not verify under the release's
 *     public key: the configured KMS key version is not the released one.
 *   Every failed-precondition and internal refusal carries
 *   `details.reason`, one of `SignLicenceRefusal`; a client reads that, never
 *   the message text.
 */
import { Licence as SignedLicence } from "@momo/types";
import { z } from "zod";
import { EnvironmentName, LicenceSchema, ProjectId } from "./licence.entity.ts";

const Payload = SignedLicence.LicencePayloadSchema.shape;

export const SignLicenceInput = z
	.object({
		projectId: ProjectId,
		client: LicenceSchema.shape.client,
		features: Payload.features,
		endsAt: Payload.endsAt,
		graceDays: Payload.graceDays.nullish(),
	})
	.strict();
export type SignLicenceInput = z.infer<typeof SignLicenceInput>;

export const SignLicenceOutput = z
	.object({
		projectId: ProjectId,
		environment: EnvironmentName,
		token: LicenceSchema.shape.token,
	})
	.strict();
export type SignLicenceOutput = z.infer<typeof SignLicenceOutput>;

export const SignLicenceRefusal = z.enum([
	"ends-at-not-future",
	"client-name-unusable",
	"signing-key-not-configured",
	"signing-key-not-installed",
	"signing-key-mismatch",
]);
export type SignLicenceRefusal = z.infer<typeof SignLicenceRefusal>;
